import type { Request, Response, Router } from 'express';

export interface McpOAuthHandlers {
  authorize(req: Request, res: Response, config: McpOAuthConfig): void | Promise<void>;
  token(req: Request, res: Response, config: McpOAuthConfig): void | Promise<void>;
  revoke(req: Request, res: Response, config: McpOAuthConfig): void | Promise<void>;
  register?(req: Request, res: Response, config: McpOAuthConfig): void | Promise<void>;
}

export interface McpOAuthConfig {
  /** Pre-provisioned OAuth client_id */
  clientId: string;
  /** Pre-provisioned OAuth client_secret */
  clientSecret: string;
  /** MCP server base URL as seen by the client (used in metadata). If not set, derived from request. */
  mcpServerBaseUrl?: string;
  /** Default scopes to request when the MCP client doesn't specify any */
  defaultScopes?: string[];
  /** If true, always use defaultScopes and ignore scopes sent by the MCP client */
  forceDefaultScopes?: boolean;
  /** Validate an OAuth token. Return true if valid, false if expired/invalid. Called on every POST /mcp when oauth=true and a token is present. */
  tokenValidator?: (token: string) => Promise<boolean>;
  /** Handler implementations for authorize, token, revoke, and optionally register */
  handlers: McpOAuthHandlers;
}

/**
 * Mount MCP OAuth endpoints required by the MCP spec:
 * - GET /.well-known/oauth-authorization-server — RFC 8414 metadata
 * - GET {mcpPath}/authorize — delegates to handlers.authorize
 * - POST {mcpPath}/token — delegates to handlers.token
 * - POST {mcpPath}/revoke — delegates to handlers.revoke
 * - POST {mcpPath}/register — delegates to handlers.register (or default pre-provisioned fallback)
 */
export function mountMcpOAuth(app: Router, mcpPath: string, config: McpOAuthConfig): void {
  const authorizeUrl = `${mcpPath}/authorize`;
  const tokenUrl = `${mcpPath}/token`;
  const revokeUrl = `${mcpPath}/revoke`;

  const registerUrl = `${mcpPath}/register`;

  console.log(`[tsoa-mcp] Mounting MCP OAuth endpoints`);
  console.log(`[tsoa-mcp]   authorize: GET ${authorizeUrl}`);
  console.log(`[tsoa-mcp]   token:     POST ${tokenUrl}`);
  console.log(`[tsoa-mcp]   revoke:    POST ${revokeUrl}`);
  console.log(`[tsoa-mcp]   register:  POST ${registerUrl}`);
  console.log(`[tsoa-mcp]   metadata:  GET /.well-known/oauth-authorization-server`);
  console.log(`[tsoa-mcp]   resource:  GET /.well-known/oauth-protected-resource`);

  // Helper to resolve external-facing base URL
  function resolveExternalUrl(req: Request): string {
    return config.mcpServerBaseUrl ?? `${req.get('x-forwarded-proto') || req.protocol}://${req.get('host')}`;
  }

  // RFC 9728 — OAuth Protected Resource Metadata
  app.get('/.well-known/oauth-protected-resource', (req: Request, res: Response) => {
    const issuer = resolveExternalUrl(req);
    res.json({
      resource: `${issuer}${mcpPath}`,
      authorization_servers: [issuer],
    });
  });

  // RFC 8414 — Authorization Server Metadata
  app.get('/.well-known/oauth-authorization-server', (req: Request, res: Response) => {
    const issuer = resolveExternalUrl(req);
    res.json({
      issuer,
      authorization_endpoint: `${issuer}${authorizeUrl}`,
      token_endpoint: `${issuer}${tokenUrl}`,
      revocation_endpoint: `${issuer}${revokeUrl}`,
      registration_endpoint: `${issuer}${mcpPath}/register`,
      response_types_supported: ['code'],
      grant_types_supported: ['authorization_code', 'refresh_token'],
      token_endpoint_auth_methods_supported: ['none'],
      code_challenge_methods_supported: ['S256'],
    });
  });

  // GET /mcp/authorize — delegate to handler
  app.get(authorizeUrl, async (req: Request, res: Response) => {
    try {
      await config.handlers.authorize(req, res, config);
    } catch (err: any) {
      if (!res.headersSent) {
        res.status(500).json({ error: 'server_error', error_description: err.message });
      }
    }
  });

  // POST /mcp/token — delegate to handler
  app.post(tokenUrl, async (req: Request, res: Response) => {
    try {
      await config.handlers.token(req, res, config);
    } catch (err: any) {
      if (!res.headersSent) {
        res.status(500).json({ error: 'server_error', error_description: err.message });
      }
    }
  });

  // POST /mcp/register — delegate to handler or use default pre-provisioned fallback
  app.post(registerUrl, async (req: Request, res: Response) => {
    try {
      if (config.handlers.register) {
        await config.handlers.register(req, res, config);
      } else {
        const { redirect_uris, client_name } = req.body;
        res.status(201).json({
          client_id: config.clientId,
          client_name: client_name || 'MCP Client',
          redirect_uris: redirect_uris || [],
          grant_types: ['authorization_code', 'refresh_token'],
          response_types: ['code'],
          token_endpoint_auth_method: 'none',
        });
      }
    } catch (err: any) {
      if (!res.headersSent) {
        res.status(500).json({ error: 'server_error', error_description: err.message });
      }
    }
  });

  // POST /mcp/revoke — delegate to handler
  app.post(revokeUrl, async (req: Request, res: Response) => {
    try {
      await config.handlers.revoke(req, res, config);
    } catch (err: any) {
      if (!res.headersSent) {
        res.status(500).json({ error: 'server_error', error_description: err.message });
      }
    }
  });
}
